'use client';

import { itemIcons } from '@/components/layout/app-sidebar';
import type { Tab } from '@/components/layout/tab-header';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { APPROVAL_ENTRY_MENU_KEYS } from '@/lib/approval-client';
import { menuCatalog } from '@/config/menu-catalog';
import { useRBAC } from '@/contexts/rbac-context';
import { useToast } from '@/hooks/use-toast';
import { notificationClient, type NotificationItem } from '@/lib/notification-client';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import { Bell, Dot } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const POLL_MS = 60_000;

const catalogItems = menuCatalog.flatMap(section =>
  section.items.map(item => ({ id: item.id, title: item.title }))
);

interface NotificationBellProps {
  onOpenTab: (item: Tab) => void;
}

/**
 * 탭 헤더 우측 벨 아이콘 + 미읽음 배지 + 알림 팝오버.
 *
 * link 해석 규칙: 첫 세그먼트가 menu key 면 탭으로 열고(권한 없으면 토스트), 독립 라우트면 navigate,
 * 둘 다 아니면 읽음 처리만 하고 "이동 미지원" 토스트. 쿼리스트링은 탭에 넘길 채널이 없어 버려진다.
 */
export function NotificationBell({ onOpenTab }: NotificationBellProps) {
  const { orgCode } = useParams();
  const navigate = useNavigate();
  const { canAccessMenu } = useRBAC();
  const { toast } = useToast();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[] | null>(null);

  // 60초 폴링. 숨김 탭은 건너뛰고 다시 보일 때 즉시 1회 갱신한다. 실패는 조용히 넘긴다(다음 주기에 복구).
  const refreshCount = useCallback(async () => {
    if (document.hidden) return;
    try {
      setUnread(await notificationClient.unreadCount());
    } catch {
      /* 다음 주기에 복구 */
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_MS);
    document.addEventListener('visibilitychange', refreshCount);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshCount);
    };
  }, [refreshCount]);

  const handleOpenChange = async (next: boolean) => {
    setOpen(next);
    if (!next) return;
    try {
      setItems(await notificationClient.list());
    } catch {
      toast({ title: '알림을 불러오지 못했습니다', variant: 'destructive' });
    }
  };

  const markRead = async (n: NotificationItem) => {
    if (n.readAt) return;
    await notificationClient.markRead(n.id);
    const readAt = new Date().toISOString();
    setItems(prev => prev && prev.map(it => (it.id === n.id ? { ...it, readAt } : it)));
    setUnread(c => Math.max(0, c - 1));
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationClient.markAllRead();
      const readAt = new Date().toISOString();
      setItems(prev => prev && prev.map(it => (it.readAt ? it : { ...it, readAt })));
      setUnread(0);
    } catch {
      toast({ title: '읽음 처리에 실패했습니다', variant: 'destructive' });
    }
  };

  const followLink = (link: string) => {
    const segment = link.split(/[/?#]/)[1] ?? '';
    // 결재는 탭이 아니라 새 창 SPA (핸드오프 §1.4). 카탈로그 키(b210 등)로 판정하지 않는 이유는
    // 링크 첫 세그먼트가 프로그램이 아니라 'approvals' 하나이기 때문이다
    if (segment === 'approvals') {
      if (!APPROVAL_ENTRY_MENU_KEYS.some(canAccessMenu)) {
        toast({ title: '접근 권한이 없는 화면입니다', description: '전자결재', variant: 'destructive' });
        return;
      }
      window.open(`/${orgCode}${link}`, '_blank', 'noopener,noreferrer');
      return;
    }
    // 탭 쉘 밖 독립 라우트(App.tsx). station-map 은 카탈로그에도 있어 먼저 걸러야 한다
    if (segment === 'station-map' || segment === 'admin') {
      navigate(`/${orgCode}${link}`);
      return;
    }
    const catalogItem = catalogItems.find(it => it.id === segment);
    if (!catalogItem) {
      toast({ title: '화면 이동 미지원', description: link });
      return;
    }
    if (!canAccessMenu(segment)) {
      toast({ title: '접근 권한이 없는 화면입니다', description: catalogItem.title, variant: 'destructive' });
      return;
    }
    onOpenTab({ id: segment, title: catalogItem.title, icon: itemIcons[segment] ?? Dot });
  };

  const handleItemClick = async (n: NotificationItem) => {
    await markRead(n).catch(() => undefined);
    setOpen(false);
    if (n.link) followLink(n.link);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className='relative h-8 w-8 p-0'
          aria-label={`읽지 않은 알림 ${unread}건`}
          title='알림'
        >
          <Bell className='size-4' />
          {unread > 0 && (
            <span
              aria-hidden
              className='absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] leading-4 text-center'
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-80 p-0'>
        <div className='flex items-center justify-between px-3 py-2 border-b border-border'>
          <span className='text-sm font-semibold'>알림</span>
          <Button variant='ghost' size='sm' className='h-7 text-xs' onClick={handleMarkAllRead} disabled={unread === 0}>
            모두 읽음
          </Button>
        </div>
        <div className='max-h-96 overflow-y-auto'>
          {items === null ? (
            <p className='p-4 text-sm text-muted-foreground text-center'>불러오는 중…</p>
          ) : items.length === 0 ? (
            <p className='p-4 text-sm text-muted-foreground text-center'>알림이 없습니다</p>
          ) : (
            <ul>
              {items.map(n => (
                <li key={n.id} className='border-b border-border last:border-b-0'>
                  <button
                    type='button'
                    onClick={() => handleItemClick(n)}
                    className={cn(
                      'w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors',
                      !n.readAt && 'bg-primary/5'
                    )}
                  >
                    <div className='flex items-start gap-2'>
                      <span
                        aria-hidden
                        className={cn('mt-1.5 size-2 rounded-full flex-shrink-0', n.readAt ? 'bg-transparent' : 'bg-primary')}
                      />
                      <div className='min-w-0 flex-1'>
                        <p className={cn('text-sm truncate', !n.readAt && 'font-semibold')}>{n.title}</p>
                        {n.body && <p className='text-xs text-muted-foreground line-clamp-2'>{n.body}</p>}
                        <p className='text-[11px] text-muted-foreground mt-0.5'>
                          {dayjs(n.createdAt).format('MM.DD HH:mm')}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
